import { formatMoney, hydrateCurrency } from "./catalogs";
import { formatUtcDateTime } from "./clock";
import {
  backupCardSummary,
  parseSnapshot,
  type SyncSnapshot,
} from "./sync";
import type { RemoteSnapshotMeta } from "./syncApi";

/** Dated local copies — separate from `ns.dispatch.store` and sync creds. */
export const BACKUP_ARCHIVE_KEY = "ns.dispatch.backups";

/** Keep the list short so localStorage quota stays usable. */
export const MAX_LOCAL_BACKUPS = 12;

/**
 * Hard ceiling for `ns.dispatch.backups` JSON. One bloated bleed-era snapshot
 * can be megabytes; 12 of those fill the WebView 5MB quota by themselves.
 */
export const MAX_BACKUP_ARCHIVE_CHARS = 1_500_000;

/** Type-to-confirm token for Settings → Backup → Restore. */
export const RESTORE_BACKUP_PHRASE = "RESTORE";

export type BackupSource = "local" | "push" | "import" | "remote" | "remote-archive" | "career-archive";

export const BACKUP_SOURCE_LABEL: Record<BackupSource, string> = {
  local: "Local",
  push: "Push copy",
  import: "Imported",
  remote: "Nanode Push",
  "remote-archive": "Nanode snapshot",
  "career-archive": "Nanode career",
};

export type ArchiveBackupSource = Exclude<BackupSource, "remote">;

export type BackupArchiveEntry = {
  id: string;
  savedAtUtc: string;
  source: ArchiveBackupSource;
  company: string;
  cashCad: number;
  currency: string;
  missionCount: number;
  includeSecrets: boolean;
  snapshot: SyncSnapshot;
};

export type BackupListRow = {
  id: string;
  source: BackupSource;
  sourceLabel: string;
  company: string;
  cashLabel: string;
  missionCount: number;
  timestampUtc: string;
  timestampLabel: string;
  includeSecrets: boolean;
  live: boolean;
};

export type StorageLike = {
  getItem(name: string): string | null | Promise<string | null>;
  setItem(name: string, value: string): unknown;
  removeItem?(name: string): unknown;
};

export function backupArchiveChars(entries: BackupArchiveEntry[]): number {
  return JSON.stringify(entries).length;
}

/** Newest-first. Stops before count or byte budget would blow localStorage. */
export function capLocalBackupEntries(
  entries: BackupArchiveEntry[],
  opts?: { maxCount?: number; maxChars?: number },
): BackupArchiveEntry[] {
  const maxCount = opts?.maxCount ?? MAX_LOCAL_BACKUPS;
  const maxChars = opts?.maxChars ?? MAX_BACKUP_ARCHIVE_CHARS;
  const out: BackupArchiveEntry[] = [];
  for (const entry of entries) {
    if (out.length >= maxCount) break;
    const next = [...out, entry];
    if (backupArchiveChars(next) > maxChars) break;
    out.push(entry);
  }
  return out;
}

export function restoreBackupPhraseOk(typed: string): boolean {
  return typed.trim().toUpperCase() === RESTORE_BACKUP_PHRASE;
}

export function newBackupId(now = new Date(), nonce = ""): string {
  const suffix = nonce || Math.random().toString(36).slice(2, 8);
  return `bk-${now.getTime().toString(36)}-${suffix}`;
}

export function backupRowFromSnapshot(
  snapshot: SyncSnapshot,
  source: BackupSource,
  opts: { id?: string; live?: boolean } = {},
): BackupListRow {
  const card = backupCardSummary(snapshot);
  return {
    id: opts.id ?? (source === "remote" ? "remote-push" : newBackupId()),
    source,
    sourceLabel: BACKUP_SOURCE_LABEL[source],
    company: card.company,
    cashLabel: card.cashLabel,
    missionCount: card.missionCount,
    timestampUtc: card.timestampUtc,
    timestampLabel: card.timestampLabel,
    includeSecrets: snapshot.includeSecrets,
    live: Boolean(opts.live || source === "remote"),
  };
}

export function backupRowFromEntry(entry: BackupArchiveEntry): BackupListRow {
  return backupRowFromSnapshot(entry.snapshot, entry.source, { id: entry.id, live: false });
}

export function entryFromSnapshot(
  snapshot: SyncSnapshot,
  source: ArchiveBackupSource,
  now = new Date(),
  nonce = "",
): BackupArchiveEntry {
  const card = backupCardSummary(snapshot);
  return {
    id: newBackupId(now, nonce),
    savedAtUtc: snapshot.exportedAtUtc || now.toISOString(),
    source,
    company: card.company,
    cashCad: card.cashCad,
    currency: card.currency,
    missionCount: card.missionCount,
    includeSecrets: snapshot.includeSecrets,
    snapshot,
  };
}

export function backupRowFromRemoteMeta(
  meta: RemoteSnapshotMeta,
  opts: { live?: boolean } = {},
): BackupListRow {
  const currency = hydrateCurrency(meta.currency);
  const live = Boolean(opts.live);
  const source: BackupSource = meta.scope === "account" ? "career-archive" : live ? "remote" : "remote-archive";
  const when = meta.savedAtUtc ? new Date(meta.savedAtUtc) : new Date(NaN);
  const valid = !Number.isNaN(when.getTime());
  return {
    id: meta.id,
    source,
    sourceLabel: BACKUP_SOURCE_LABEL[source],
    company: meta.company || "—",
    cashLabel: `${currency} ${formatMoney(meta.cashCad, currency)}`,
    missionCount: meta.missionCount,
    timestampUtc: valid ? when.toISOString() : meta.savedAtUtc,
    timestampLabel: valid ? formatUtcDateTime(when) : meta.savedAtUtc || "—",
    includeSecrets: meta.includeSecrets,
    live,
  };
}

function sameRemoteCard(meta: RemoteSnapshotMeta, remote: SyncSnapshot): boolean {
  const card = backupCardSummary(remote);
  return (
    meta.scope === "company" &&
    meta.savedAtUtc === card.timestampUtc &&
    meta.company === card.company &&
    meta.cashCad === card.cashCad &&
    meta.missionCount === card.missionCount
  );
}

export function mergeBackupRows(
  local: BackupArchiveEntry[],
  remote?: SyncSnapshot | null,
  dated: RemoteSnapshotMeta[] = [],
): BackupListRow[] {
  const rows: BackupListRow[] = [];
  if (remote) {
    rows.push(backupRowFromSnapshot(remote, "remote", { id: "remote-push", live: true }));
  }
  for (const meta of dated) {
    if (remote && sameRemoteCard(meta, remote)) continue;
    rows.push(backupRowFromRemoteMeta(meta));
  }
  rows.push(...local.map(backupRowFromEntry));
  return rows;
}

export function parseBackupArchive(input: unknown): BackupArchiveEntry[] {
  let value = input;
  if (typeof input === "string") {
    try {
      value = JSON.parse(input) as unknown;
    } catch {
      return [];
    }
  }
  if (!Array.isArray(value)) return [];
  const out: BackupArchiveEntry[] = [];
  for (const item of value) {
    const entry = hydrateArchiveEntry(item);
    if (entry) out.push(entry);
  }
  return out;
}

function hydrateArchiveEntry(item: unknown): BackupArchiveEntry | null {
  if (!item || typeof item !== "object") return null;
  const rec = item as Record<string, unknown>;
  const parsed = parseSnapshot(rec.snapshot);
  if (!parsed.ok) return null;
  const source: ArchiveBackupSource = rec.source === "push" || rec.source === "import" ? rec.source : "local";
  const card = backupCardSummary(parsed.snapshot);
  return {
    id: typeof rec.id === "string" && rec.id.trim() ? rec.id : newBackupId(),
    savedAtUtc: typeof rec.savedAtUtc === "string" ? rec.savedAtUtc : parsed.snapshot.exportedAtUtc,
    source,
    company: card.company,
    cashCad: card.cashCad,
    currency: card.currency,
    missionCount: card.missionCount,
    includeSecrets: parsed.snapshot.includeSecrets,
    snapshot: parsed.snapshot,
  };
}

export function listLocalBackups(storage: StorageLike | null | undefined): BackupArchiveEntry[] {
  if (!storage) return [];
  const raw = storage.getItem(BACKUP_ARCHIVE_KEY);
  if (typeof raw !== "string" || !raw) return [];
  return parseBackupArchive(raw);
}

function writeArchive(entries: BackupArchiveEntry[], storage: StorageLike): boolean {
  const capped = capLocalBackupEntries(entries);
  try {
    storage.setItem(BACKUP_ARCHIVE_KEY, JSON.stringify(capped));
    return true;
  } catch {
    if (capped.length <= 1) {
      try {
        storage.setItem(BACKUP_ARCHIVE_KEY, "[]");
        return false;
      } catch {
        try {
          storage.removeItem?.(BACKUP_ARCHIVE_KEY);
        } catch {
          /* quota / private mode */
        }
        return false;
      }
    }
    return writeArchive(capped.slice(0, -1), storage);
  }
}

/** Drop oldest / oversized leftover copies so hydrate/save can use the quota. */
export function compactLocalBackups(storage: StorageLike | null | undefined): number {
  if (!storage) return 0;
  const current = listLocalBackups(storage);
  const raw = storage.getItem(BACKUP_ARCHIVE_KEY);
  const capped = capLocalBackupEntries(current);
  const overBudget = typeof raw === "string" && raw.length > MAX_BACKUP_ARCHIVE_CHARS;
  if (capped.length === current.length && !overBudget) return 0;
  writeArchive(capped, storage);
  return Math.max(0, current.length - listLocalBackups(storage).length);
}

export function recordLocalBackup(
  snapshot: SyncSnapshot,
  source: ArchiveBackupSource,
  storage: StorageLike | null | undefined,
  now = new Date(),
): BackupArchiveEntry | null {
  if (!storage) return null;
  const entry = entryFromSnapshot(snapshot, source, now);
  const next = capLocalBackupEntries([entry, ...listLocalBackups(storage)]);
  if (!next.some((row) => row.id === entry.id)) return null;
  if (!writeArchive(next, storage)) return null;
  return entry;
}

export function getLocalBackup(id: string, storage: StorageLike | null | undefined): BackupArchiveEntry | null {
  if (!id) return null;
  return listLocalBackups(storage).find((entry) => entry.id === id) ?? null;
}

export function removeLocalBackup(id: string, storage: StorageLike | null | undefined): boolean {
  if (!storage) return false;
  const next = listLocalBackups(storage).filter((entry) => entry.id !== id);
  return writeArchive(next, storage);
}
